// src/components/visuals/ConsciousNetworkVisual.tsx

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { Brain, Users, Archive, Heart } from 'lucide-react';

// --- Data & Styling Constants ---
const HIGHLIGHT_COLOR = '#34d399'; // emerald-400
const IDLE_COLOR = '#475569';      // slate-600

const nodeData = [
  { id: 'agent', icon: <Archive strokeWidth={1.5} />, pos: { x: '25%', y: '25%' } },
  { id: 'community', icon: <Users strokeWidth={1.5} />, pos: { x: '75%', y: '25%' } },
  { id: 'value', icon: <Heart strokeWidth={1.5} />, pos: { x: '25%', y: '75%' } },
  { id: 'user', icon: <span className="font-bold text-lg">U</span>, pos: { x: '75%', y: '75%' } },
];
const centerPos = { x: '50%', y: '50%' };

// --- Animation Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const nodeVariants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: { opacity: 1, scale: 1 },
};
const pathVariants = {
  hidden: { pathLength: 0 },
  visible: { pathLength: 1, transition: { duration: 0.4, ease: 'easeOut' } },
  exit: { pathLength: 0, transition: { duration: 0.2, ease: 'easeIn' } },
};

export const ConsciousNetworkVisual: React.FC = () => {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [showPing, setShowPing] = useState(false);

  // --- ENHANCEMENT: Parallax Background ---
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const backgroundX = useTransform(mouseX, [-100, 100], ['5%', '-5%']);
  const backgroundY = useTransform(mouseY, [-100, 100], ['5%', '-5%']);
  
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const { clientX, clientY, currentTarget } = event;
    const { left, top, width, height } = currentTarget.getBoundingClientRect();
    const x = clientX - left - width / 2;
    const y = clientY - top - height / 2;
    mouseX.set(x);
    mouseY.set(y);
  };

  const connectionPath = useMemo(() => {
    if (!hoveredNodeId) return null;
    const node = nodeData.find(n => n.id === hoveredNodeId);
    if (!node) return null;
    const pathD = `M ${node.pos.x},${node.pos.y} Q ${node.pos.x},${centerPos.y} ${centerPos.x},${centerPos.y}`;
    
    // --- ENHANCEMENT: Energy Flow Gradient & Ping on Arrival ---
    const uniqueId = `grad-${hoveredNodeId}`;
    return (
      <>
        <defs>
          <linearGradient id={uniqueId} x1={node.pos.x} y1={node.pos.y} x2={centerPos.x} y2={centerPos.y} gradientUnits="objectBoundingBox">
            <stop offset="0%" stopColor={HIGHLIGHT_COLOR} />
            <stop offset="100%" stopColor={HIGHLIGHT_COLOR} stopOpacity="0.2"/>
          </linearGradient>
        </defs>
        <motion.path 
          d={pathD} fill="none" 
          stroke={`url(#${uniqueId})`}
          strokeWidth="1.5" 
          variants={pathVariants}
          onAnimationComplete={() => setShowPing(true)}
        />
      </>
    );
  }, [hoveredNodeId]);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.5 }}
      onMouseMove={handleMouseMove}
      className="relative w-full h-96 rounded-2xl border border-slate-800 bg-slate-900 overflow-hidden cursor-crosshair"
    >
      <motion.div
        className="absolute inset-[-10%] bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:2rem_2rem]"
        style={{ x: backgroundX, y: backgroundY }}
      />
      
      <svg width="100%" height="100%" className="absolute inset-0">
        <AnimatePresence onExitComplete={() => setShowPing(false)}>
          {connectionPath}
        </AnimatePresence>
      </svg>
      
      {/* --- ENHANCED: Central "Brain" Node --- */}
      <motion.div
        variants={nodeVariants}
        className="absolute"
        style={{ left: centerPos.x, top: centerPos.y, transform: 'translate(-50%, -50%)' }}
      >
        <motion.div
          className="relative w-20 h-20 rounded-2xl flex items-center justify-center"
          animate={{ scale: hoveredNodeId ? 1.1 : 1 }}
        >
          {/* Pulsating Aura */}
          <motion.div 
            className="absolute inset-0 rounded-2xl"
            animate={{
              boxShadow: `0 0 0px ${hoveredNodeId ? HIGHLIGHT_COLOR : IDLE_COLOR}, 0 0 20px ${hoveredNodeId ? HIGHLIGHT_COLOR : 'transparent'}`,
              borderColor: hoveredNodeId ? HIGHLIGHT_COLOR : IDLE_COLOR
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          />
           <div className="absolute inset-0 border rounded-2xl" style={{ borderColor: 'inherit' }}/>
          {/* Inner Rotating Core */}
          <div className="absolute inset-0 rounded-2xl opacity-40 overflow-hidden">
            <motion.div 
              className="absolute w-[200%] h-[200%] -translate-x-1/2 -translate-y-1/2 bg-[conic-gradient(from_0deg,transparent_70%,_#34d399)]"
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 8, ease: 'linear'}}
            />
          </div>
          <Brain strokeWidth={1.5} className="z-10 transition-colors" style={{color: hoveredNodeId ? HIGHLIGHT_COLOR : IDLE_COLOR }} />
           {/* Connection Ping */}
          <AnimatePresence>
            {showPing && <motion.div className='absolute rounded-full border-2'
              initial={{ width: 0, height: 0, opacity: 1, borderColor: HIGHLIGHT_COLOR }}
              animate={{ width: "120%", height: "120%", opacity: 0, transition: { duration: 0.4 } }}
             />}
          </AnimatePresence>
        </motion.div>
      </motion.div>

      {/* --- ENHANCED: Peripheral Nodes --- */}
      {nodeData.map((node) => (
        <motion.div
          key={node.id}
          variants={nodeVariants}
          onHoverStart={() => setHoveredNodeId(node.id)}
          onHoverEnd={() => setHoveredNodeId(null)}
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 6 + Math.random() * 3, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute w-16 h-16 rounded-2xl flex items-center justify-center cursor-pointer"
          style={{ left: node.pos.x, top: node.pos.y, transform: 'translate(-50%, -50%)' }}
        >
          {/* Spotlight Effect */}
          <motion.div
            className="absolute inset-[-50%] rounded-full"
            animate={{ background: `radial-gradient(circle, ${HIGHLIGHT_COLOR}20 0%, transparent 50%)`,
              opacity: hoveredNodeId === node.id ? 1 : 0
            }}
          />
          <motion.div 
            className="absolute inset-0 border rounded-2xl"
            animate={{ borderColor: hoveredNodeId === node.id ? HIGHLIGHT_COLOR : IDLE_COLOR }}
          />
          {/* Activated Icon */}
          <motion.div
            className="flex items-center justify-center z-10"
            animate={{
              color: hoveredNodeId === node.id ? HIGHLIGHT_COLOR : IDLE_COLOR,
              scale: hoveredNodeId === node.id ? 1.15 : 1,
            }}
          >
            {node.icon}
          </motion.div>
        </motion.div>
      ))}
    </motion.div>
  );
};